/**
 * App.jsx
 * Comprehensive Quality Engineering Implementation Intake Terminal Console Shell Layout
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import AuditPortal from './AuditPortal';
import ServiceInstallChecklist from './InstallChecklist';
import CaseQueue from './CaseQueue';
import InstallComplete from './InstallComplete';
import './rapid-implementation-form.css';
import {
  MODS, GO, GL, GB, GF, TD, TBC, TTC, CT_LABELS, DEAL_FIELDS,
  api, uid, nowStr, todayStr, recordAuditLog, downloadFile
} from '../../api/rapid-imlementation-workflow-api';
import {
  currentRole, isTabAllowed, defaultTabForRole, canCreateIntake, CHROME_TABS
} from './roleAccess';

export default function App() {
  const role = currentRole();

  // Navigation Router Switch Node ('intake' | 'queue' | 'checklist' | 'audit')
  // AE sign-off deep links (/?id=...&t=...) open the intake review regardless of
  // role gates — the ae-review-only body class already restricts what renders.
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('id') && params.get('t')) return 'intake';
    return defaultTabForRole(currentRole());
  });

  const routeAndResetIntakeForm = (targetTab) => {
    if (!isTabAllowed(role, targetTab)) return;
    if(targetTab !== 'intake') {
      clearSystemEcosystemState();
    }
    setActiveTab(targetTab);
};
  // Initialization Hook Safety Latch
  const initializationLock = useRef(false);

  // Core Data Scopes Register Models
  const [intakeId, setIntakeId] = useState(null);
  const [signoffToken, setSignoffToken] = useState(null);
  const [isSignoffMode, setIsSignoffMode] = useState(false);
  const [currentIntakeStatus, setCurrentIntakeStatus] = useState('draft');
  const [showAeModal, setShowAeModal] = useState(false);
  const [finalSubmitComplete, setFinalSubmitComplete] = useState(false);
  const [currentInaccuracy, setCurrentInaccuracy] = useState(null);
  const [showUploadZone, setShowUploadZone] = useState(true);
  
  // Controlled Form Inputs
  const [formState, setFormState] = useState({
    quoteNum: '', oppName: '', ae: '', effDate: '', endDate: '',
    tcv: '', parent: '', region: '', sfAcctId: '', ffNotes: '',
    cnotes: '', repQuote: '', trialStart: '', trialEnd: ''
  });
  
  const [contractType, setContractType] = useState('');
  const [sites, setSites] = useState([]);
  const [selectedSiteIdx, setSelectedSiteIdx] = useState(null);
  
  // Interface Alerts & Dynamic AI Color Glow Tracking Flags
  const [aiAlert, setAiAlert] = useState({ show: false, msg: '' });
  const [aiSitesAlert, setAiSitesAlert] = useState({ show: false, msg: '' });
  const [aiHighlightedFields, setAiHighlightedFields] = useState({});
  const [lookupStatus, setLookupStatus] = useState({ color: '', text: '' });
  const [uploadState, setUploadState] = useState({ parsing: false, pct: 0, title: '', sub: '', filename: '' });
  const [extractedData, setExtractedData] = useState(null);
  const [isQuoteRevisionMode, setIsQuoteRevisionMode] = useState(false);
  const [quoteRevisionDirty, setQuoteRevisionDirty] = useState(false);
  
  // Modal Overlays Tracking Triggers (Unified under showPreview frame)
  const [showPreview, setShowPreview] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [inaccuracyComment, setInaccuracyComment] = useState('');
  const [showInaccuracyBox, setShowInaccuracyBox] = useState(false);
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);
  const [overrideInFlight, setOverrideInFlight] = useState(false);
  
  // Handoff Processing States
  const [aeEmail, setAeEmail] = useState('');
  const [aeNotifName, setAeNotifName] = useState('');
  const [aeLinkGenerated, setAeLinkGenerated] = useState(false);
  const [generatedDeeplink, setGeneratedDeeplink] = useState('');
  
  // Signature Certified Track Handshakes
  const [aeSigName, setAeSigName] = useState('');
  const [aeSigTitle, setAeSigTitle] = useState('Account Executive');
  const [aeSigDate, setAeSigDate] = useState(todayStr());
  const [aeSignedOff, setAeSignedOff] = useState(false);
  const [wfStep, setWfStep] = useState(1);
  const [wfAuditLog, setWfAuditLog] = useState([]);
  const [caseNumber, setCaseNumber] = useState('');
  const [finalSubmitInFlight, setFinalSubmitInFlight] = useState(false);
  
  const saveTimer = useRef(null);

  // Unconditional memo tracking for parsing AI extracted telemetry tags securely
  const detectedModules = useMemo(() => {
    if (!extractedData || !extractedData.detectedModuleIds) return [];
    return extractedData.detectedModuleIds
      .map(mId => MODS.find(m => m.id === mId))
      .filter(Boolean);
  }, [extractedData]);

  // Smooth Scroll Navigation Helper
  const handleScrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Live Aggregate Ecosystem Calculations Engine
  const metrics = useMemo(() => {
    const totalSites = sites.length;
    const totalActivations = sites.reduce((acc, s) => acc + Object.keys(s.mods || {}).length, 0);
    const counters = { Hub: 0, Spoke: 0, 'CAH/FSED': 0, MSTU: 0, Clinic: 0 };
    sites.forEach(s => { if (counters[s.type] !== undefined) counters[s.type]++; });
    return { totalSites, totalActivations, ...counters };
  }, [sites]);

  // Preview matrix: only the modules actually selected across any site, kept in
  // catalog order, plus how many columns each group spans (reuses Step-4 GO/GB/GF).
  const previewMatrix = useMemo(() => {
    const activeMods = MODS.filter(m => sites.some(s => s.mods && s.mods[m.id]));
    const groupSpans = {};
    activeMods.forEach(m => { groupSpans[m.g] = (groupSpans[m.g] || 0) + 1; });
    return { activeMods, groupSpans, distinctModules: activeMods.length };
  }, [sites]);

  // Missing-required-fields banner shown in the review modal.
  const previewWarnings = useMemo(() => {
    const w = [];
    if (!formState.quoteNum) w.push('Quote number not entered');
    if (!formState.ae) w.push('Account Executive name not entered');
    if (!formState.parent) w.push('Parent account not entered');
    const emptySites = sites.filter(s => !s.name);
    if (emptySites.length) w.push(`${emptySites.length} site row(s) have no name`);
    if (contractType === 'evaluation' && (!formState.trialStart || !formState.trialEnd)) {
      w.push('Trial start/end dates not fully entered');
    }
    return w;
  }, [formState, sites, contractType]);

  // Progressive Registration Bar Ratio Multipliers
  const progressPercentage = useMemo(() => {
    if (finalSubmitComplete) return 100;
    let componentsFilled = 0;
    if (contractType) componentsFilled++;
    if (metrics.totalSites > 0) componentsFilled++;
    if (formState.quoteNum) componentsFilled++;
    if (formState.parent) componentsFilled++;
    if (formState.ae) componentsFilled++;
    return Math.round((componentsFilled / 5) * 80);
  }, [formState, contractType, metrics.totalSites, finalSubmitComplete]);

  // Lifecycle Initialization Routines
  useEffect(() => {
    if (initializationLock.current) return;
    initializationLock.current = true;

    recordAuditLog({ category: 'navigation', action: 'Viewed Implementation Intake', detail: 'Opened Implementation Intake Form.', target: 'Implementation Intake', route: 'GET /', quoteNum: '', intakeId: '' }).catch(() => {});

    // const initializePortal = async () => {
    //     try {
    //     await api.logPortalAccess('Implementation Intake Form', '/');
    //     } catch (err) {
    //     console.warn('[Telemetry Core] Failed to register portal access event:', err.message);
    //     }
    // };
    //  initializePortal().then(() => {
    // });
    const params = new URLSearchParams(window.location.search);
    let id = params.get('id');
    const token = params.get('t');
    const reviewParam = params.get('review') === '1';
    const revisionParam = params.get('revision') === 'quote';
    
    setIsQuoteRevisionMode(revisionParam);
    
    if (token && id) {
      api.validateSignoffToken(token)
        .then(d => {
          if (!d.valid) { alert('This sign-off link is invalid or has already been used.'); return; }
          setIntakeId(id);
          setSignoffToken(token);
          setIsSignoffMode(true);
          document.body.classList.add('ae-review-only');
          hydrateEcosystem(d.intake);
          setShowPreview(true);
        })
        .catch(() => alert('Could not validate sign-off link.'));
    } else if (id) {
      setIntakeId(id);
      api.getIntake(id)
        .then(d => {
          hydrateEcosystem(d);
          if (reviewParam) setShowPreview(true);
        })
        .catch(() => alert('Could not find contract in datbase.'));
    }
  }, [intakeId]);

  // System Autosave Framework Link
  useEffect(() => {
    if (!intakeId || isSignoffMode || finalSubmitComplete || activeTab !== 'intake') return;
    // AE-approved cases are read-only on the server (PUT returns 409) — don't autosave them.
    if (['signed', 'ready_to_install', 'partially_installed', 'install_complete'].includes(currentIntakeStatus)) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const statePayload = { deal: formState, contractType, sites, revisionFromNewQuote: isQuoteRevisionMode && quoteRevisionDirty };
      api.updateIntake(intakeId, statePayload)
        .then(() => { if (isQuoteRevisionMode && quoteRevisionDirty) setQuoteRevisionDirty(false); })
        .catch(e => console.warn('Auto-save failed:', e.message));
    }, 1200);
    return () => clearTimeout(saveTimer.current);
  }, [formState, contractType, sites, intakeId, isSignoffMode, finalSubmitComplete, activeTab, isQuoteRevisionMode, quoteRevisionDirty, currentIntakeStatus]);

  const createFreshDraft = async () => {
    try {
      // 1. Await the API network operation execution cleanly
      const d = await api.createIntake();
      
      // 2. Extract the new record identifier parameter
      const newId = d.id;
      
      // 3. Update the React state framework wrapper
      setIntakeId(newId);
      
      // 4. Cache it inside your sessionStorage layer boundary
      sessionStorage.setItem('active_intake_id', newId);
      
      // 5. CRITICAL: Return the id value so processUploadedContract can capture it!
      return newId;
      
    } catch (error) {
      console.error('Failed to generate fresh internal data draft node:', error);
      throw error; // Propagates the exception up to the parent caller try/catch block
    }
  };

  const hydrateEcosystem = (data) => {
    setCurrentIntakeStatus(data.status || 'draft');
    if (data.deal) {
      setFormState(prev => {
        const next = { ...prev };
        DEAL_FIELDS.forEach(f => { if (data.deal[f] !== undefined) next[f] = data.deal[f]; });
        return next;
      });
    }
    if (data.contractType) setContractType(data.contractType);
    if (data.sites) setSites(data.sites);
    setCurrentInaccuracy(data.inaccuracy || null);
  };

  // Human-readable label for the review-loop statuses (used in the preview banner/footer).
  const statusText = (st) => ({
    inaccurate_data_claim: 'Inaccurate Data Claim',
    new_quote_needed: 'New Quote Needed',
    needs_im_revision: 'Needs IM Revision',
  }[st] || (st || '').replace(/_/g, ' '));

  const handleInputChange = (field, value) => {
    if (isQuoteRevisionMode) setQuoteRevisionDirty(true);
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  const lookupQuoteSF = () => {
    const q = formState.quoteNum.trim();
    if (!q) { setLookupStatus({ color: 'var(--red)', text: 'Enter a quote number first.' }); return; }
    
    recordAuditLog({ category: 'quote', action: 'Used quote lookup', detail: `Looked up quote ${q}`, target: q, quoteNum: q, intakeId: intakeId || '' });
    setLookupStatus({ color: 'var(--blue)', text: '⟳ Looking up in Salesforce…' });
    
    setTimeout(() => {
      const parts = q.split('_');
      const hint = parts.length > 1 ? parts.slice(1, -1).join(' ').replace(/([A-Z])/g, ' $1').trim() : q;
      setFormState(prev => ({ ...prev, oppName: `${hint} — RapidAI Implementation`, parent: hint }));
      setLookupStatus({ color: 'var(--green)', text: '✓ Populated from Salesforce · review and confirm' });
    }, 700);
  };

  /* ══════════════════════════════════════════════════
     REFACTORED INTAKE OPEN MODAL LOGIC MAPPING
     ══════════════════════════════════════════════════ */
  const triggerReviewPreview = () => {
    // 1. Validate form has enough data first
    if (!contractType) { 
      alert('Please select a contract type in Step 3 first.'); 
      return; 
    }
    if (!sites || sites.length === 0) { 
      alert('Please add at least one site in Step 4 first.'); 
      return; 
    }

    // 2. Pre-fill AE name from form field if available
    const aeVal = formState.ae || '';
    if (!aeNotifName && aeVal) {
      setAeNotifName(aeVal);
    }

    // 3. Log the audit event trace record exactly matching original requirements
    // recordAuditLog({
    //   category: 'signoff',
    //   action: 'Opened AE notification',
    //   detail: 'Opened AE notification modal.',
    //   target: intakeId || '',
    //   quoteNum: formState.quoteNum,
    //   intakeId: intakeId || ''
    // });

    // 4. Reveal single blurred chassis template
    setShowAeModal(true);
    document.body.style.overflow = 'hidden';
  };

  // Drag and Drop Parsing Engine Pipeline
  const processUploadedContract = async (file) => {
    if (!file) return;
    setShowUploadZone(false);
    setUploadState(p => ({ ...p, parsing: true, pct: 10, title: `Reading ${file.name}…`, filename: file.name }));
    let intakeID;
    try {
      intakeID = await createFreshDraft(); 
    } catch (err) {
      setUploadState(p => ({ ...p, parsing: false }));
      alert(`Failed to initialize new intake template: ${err.message}`);
      return;
    }
    
    // Fallback safeguard to check sessionStorage if your setup stores it there
    if (!intakeID) {
      intakeID = sessionStorage.getItem('active_intake_id');
    }
    //recordAuditLog({ category: 'ai', action: 'Uploaded contract for parsing', detail: `Uploaded contract "${file.name}"`, quoteNum: formState.quoteNum, intakeId: intakeID || '' });
    
    const reader = new FileReader();
    const isBinary = file.type === 'application/pdf' || file.type.startsWith('image/');
    
    reader.onload = () => {
      const base64Content = isBinary ? reader.result.split(',')[1] : reader.result;
      let stepIdx = 0;
      const pipelineSteps = [
        [30, 'Sending to Claude AI…'],
        [55, 'Identifying parties and dates…'],
        [75, 'Matching modules to RapidAI catalog…'],
        [95, 'Populating layout records…']
      ];

      const interval = setInterval(() => {
        if (stepIdx < pipelineSteps.length) {
          const currentStep = pipelineSteps[stepIdx];
          if (currentStep) {
            setUploadState(p => ({ ...p, pct: currentStep[0], title: currentStep[1] }));
          }
          stepIdx++;
        } else {
          clearInterval(interval);
        }
      }, 1000);

      api.parseContractServer(base64Content, isBinary ? file.type : null, file.name, intakeID)
        .then(res => {
          clearInterval(interval);
          let payload;
          try {
            const cleanText = res.content[0].text.replace(/```json|```/g, '').trim();
            payload = JSON.parse(cleanText);
          } catch {
            payload = { confidence: 'low', warnings: ['Could not parse engine telemetry response.'] };
          }
          
          setUploadState(p => ({ ...p, pct: 100, title: 'Done — structural parameters extracted' }));
          setTimeout(() => {
            setUploadState(p => ({ ...p, parsing: false }));
            finalizeParsedContract(payload, file.name, intakeID);
          }, 400);
        })
        .catch(err => {
          clearInterval(interval);
          setUploadState(p => ({ ...p, parsing: false }));
          alert(`Telemetry Engine error: ${err.message}`);
        });
    };
    
    if (isBinary) reader.readAsDataURL(file);
    else reader.readAsText(file);
  };

  // Checks the parsed quote number against existing active cases before applying
  // the extracted data — a match opens the duplicate-override prompt instead, so the
  // form (and therefore the autosave PUT) never carries a conflicting quote number.
  const finalizeParsedContract = async (payload, filename, draftId) => {
    const qn = (payload.quoteNumber || payload.quoteNum || '').trim();
    if (!qn) { applyExtractedTelemetry(payload, filename); return; }
    let lookup = null;
    try {
      lookup = await api.lookupAnyIntake(qn, draftId);
    } catch { lookup = null; } // lookup failure → proceed as before; the PUT 409 remains the backstop
    if (lookup?.exists && lookup.intake) {
      recordAuditLog({ category: 'quote', action: 'Duplicate quote detected on upload', detail: `Parsed quote ${qn} from "${filename}" matches existing case ${lookup.intake.id} (${lookup.intake.statusLabel}).`, target: qn, quoteNum: qn, intakeId: draftId || '' });
      setDuplicatePrompt({ payload, filename, existing: lookup.intake, matchCount: (lookup.matches || []).length, draftId, quoteNum: qn });
      return;
    }
    applyExtractedTelemetry(payload, filename);
  };

  const discardParsedUpload = () => {
    setExtractedData(null);
    setAiHighlightedFields({});
    setAiAlert({ show: false, msg: '' });
    setAiSitesAlert({ show: false, msg: '' });
    setUploadState({ parsing: false, pct: 0, title: '', sub: '', filename: '' });
    setShowUploadZone(true);
  };

  const confirmDuplicateOverride = async () => {
    if (!duplicatePrompt || overrideInFlight) return;
    const { payload, filename, existing, draftId, quoteNum } = duplicatePrompt;
    setOverrideInFlight(true);
    try {
      const res = await api.overrideDuplicateQuote(draftId || intakeId, quoteNum);
      recordAuditLog({ category: 'quote', action: 'Overwrote duplicate quote case', detail: `Archived ${(res.archivedIds || []).length} case(s) [${(res.archivedIds || []).join(', ') || 'none'}] (was ${existing.statusLabel}) and deleted ${res.deletedChecklists || 0} install-checklist record(s); starting over from "${filename}".`, target: quoteNum, quoteNum, intakeId: draftId || intakeId || '' });
      setDuplicatePrompt(null);
      applyExtractedTelemetry(payload, filename);
    } catch (e) {
      // requestJson already alerted — keep the modal open so the user can retry.
      console.warn('Duplicate override failed:', e.message);
    } finally {
      setOverrideInFlight(false);
    }
  };

  const openExistingCaseInstead = async () => {
    if (!duplicatePrompt || overrideInFlight) return;
    const { existing, quoteNum } = duplicatePrompt;
    try {
      // Fetch BEFORE switching ids so the autosave effect never sees the new id with stale form state.
      const data = await api.getIntake(existing.id);
      window.history.replaceState(null, '', `/?id=${encodeURIComponent(existing.id)}`);
      sessionStorage.setItem('active_intake_id', existing.id);
      hydrateEcosystem(data); // batched with setIntakeId by React 18 → autosave only sees consistent state
      setIntakeId(existing.id);
      discardParsedUpload();
      setDuplicatePrompt(null);
      recordAuditLog({ category: 'quote', action: 'Kept existing case for duplicate quote', detail: `Discarded uploaded contract and opened existing case ${existing.id} (${existing.statusLabel}) for quote ${quoteNum}.`, target: quoteNum, quoteNum, intakeId: existing.id });
    } catch (e) {
      console.warn('Could not open existing case:', e.message);
    }
  };

  const dismissDuplicatePrompt = () => {
    if (!duplicatePrompt || overrideInFlight) return;
    const { draftId, quoteNum } = duplicatePrompt;
    discardParsedUpload();
    setDuplicatePrompt(null);
    recordAuditLog({ category: 'quote', action: 'Dismissed duplicate-quote prompt', detail: `Discarded uploaded contract for quote ${quoteNum} without overwriting or opening the existing case.`, target: quoteNum, quoteNum, intakeId: draftId || intakeId || '' });
  };

  const applyExtractedTelemetry = (data, name) => {
    let fieldsCount = 0;
    const nextForm = { ...formState };
    const highlights = {};
    
    const setFieldWithHighlight = (fieldKey, payloadVal) => {
      if (payloadVal) {
        nextForm[fieldKey] = payloadVal;
        highlights[fieldKey] = true;
        fieldsCount++;
      }
    };

    setFieldWithHighlight('quoteNum', data.quoteNumber || data.quoteNum);
    setFieldWithHighlight('oppName', data.opportunityName);
    setFieldWithHighlight('ae', data.accountExecutive);
    setFieldWithHighlight('parent', data.parentAccount);
    setFieldWithHighlight('region', data.region);
    setFieldWithHighlight('effDate', data.effectiveDate);
    setFieldWithHighlight('endDate', data.endDate);
    setFieldWithHighlight('tcv', data.totalContractValue);
    setFieldWithHighlight('ffNotes', data.ffNotes);
    setFieldWithHighlight('trialEndDate', data.trialEndDate);
    setFieldWithHighlight('cnotes', data.contractNotes);
    if (data.trialStartDate) nextForm.trialStart = data.trialStartDate;
    if (data.trialEndDate) nextForm.trialEnd = data.trialEndDate;
    
    setFormState(nextForm);
    setAiHighlightedFields(highlights);
    if (data.contractType) setContractType(data.contractType);
    
    if (data.sites && data.sites.length) {
      const structuralSites = data.sites.map(s => {
        const activeMap = {};
        (s.moduleIds || []).forEach(mId => { activeMap[mId] = true; });
        if (!Object.keys(activeMap).length && s.type && TD[s.type]) {
          TD[s.type].forEach(mId => { activeMap[mId] = true; });
        }
        return { id: uid(), name: s.name || '', type: s.type || '', region: s.region || '', mods: activeMap, sc: s.scanners || '', notes: s.notes || '' };
      });
      setSites(structuralSites);
      setAiSitesAlert({ show: true, msg: `<strong>${structuralSites.length} site${structuralSites.length === 1 ? '' : 's'} auto-populated</strong> from contract with modules pre-checked. Review each row.` });
      fieldsCount += structuralSites.length;
    }
    
    setAiAlert({ show: true, msg: `<strong>${fieldsCount} field${fieldsCount === 1 ? '' : 's'} auto-filled</strong> from "${name}" — highlighted in green below. Review and confirm before submitting.` });
    setExtractedData(data);
    recordAuditLog({ category: 'ai', action: 'Applied parsed contract', detail: `Auto-filled ${fieldsCount} field(s) from "${name}".`, target: name, quoteNum: data.quoteNumber || data.quoteNum || formState.quoteNum, intakeId: intakeId || '' });
    if (isQuoteRevisionMode) setQuoteRevisionDirty(true);
  };


  const toggleMatrixCheckbox = (sIdx, mId) => {
    const nextSites = [...sites];
    const site = nextSites[sIdx];
    if (site.mods[mId]) {
      delete site.mods[mId];
    } else {
      site.mods[mId] = true;
    }
    setSites(nextSites);
  };

  const addMatrixRow = (type) => {
    const defaultModules = {};
    if (type && TD[type]) TD[type].forEach(mId => { defaultModules[mId] = true; });
    const newRow = { id: uid(), name: '', type: type || '', region: '', mods: defaultModules, sc: '', notes: '' };
    const nextSites = [...sites, newRow];
    setSites(nextSites);
    setSelectedSiteIdx(nextSites.length - 1);
    recordAuditLog({ category: 'matrix', action: 'Added site row', detail: `Appended structural facility type profile: ${type || 'Blank'}`, quoteNum: formState.quoteNum, intakeId: intakeId || '' });
  };

  const deleteMatrixRow = (idx) => {
    const removed = sites[idx];
    const nextSites = sites.filter((_, i) => i !== idx);
    setSites(nextSites);
    setSelectedSiteIdx(null);
    recordAuditLog({ category: 'matrix', action: 'Deleted site row', detail: `Removed site "${removed?.name || 'Unnamed'}".`, quoteNum: formState.quoteNum, intakeId: intakeId || '' });
  };

  const duplicateMatrixRow = (idx) => {
    if (idx === null) return;
    const source = sites[idx];
    const copy = { ...source, id: uid(), name: source.name ? `${source.name} (copy)` : '', mods: { ...source.mods } };
    const nextSites = [...sites];
    nextSites.splice(idx + 1, 0, copy);
    setSites(nextSites);
    setSelectedSiteIdx(idx + 1);
    recordAuditLog({ category: 'matrix', action: 'Duplicated site row', detail: `Duplicated site "${source?.name || 'Unnamed'}".`, quoteNum: formState.quoteNum, intakeId: intakeId || '' });
  };

  const bulkCheckAllModules = () => {
    if (selectedSiteIdx === null) return;
    const nextSites = [...sites];
    MODS.forEach(m => { nextSites[selectedSiteIdx].mods[m.id] = true; });
    setSites(nextSites);
    recordAuditLog({ category: 'matrix', action: 'Checked all modules', detail: `Enabled all modules for "${nextSites[selectedSiteIdx]?.name || 'Unnamed'}".`, quoteNum: formState.quoteNum, intakeId: intakeId || '' });
  };

  const bulkClearAllModules = () => {
    if (selectedSiteIdx === null) return;
    const siteName = sites[selectedSiteIdx]?.name || 'Unnamed';
    const nextSites = [...sites];
    nextSites[selectedSiteIdx].mods = {};
    setSites(nextSites);
    recordAuditLog({ category: 'matrix', action: 'Cleared all modules', detail: `Cleared all modules for "${siteName}".`, quoteNum: formState.quoteNum, intakeId: intakeId || '' });
  };

  const submitInaccuracyClaim = () => {
    if (!inaccuracyComment.trim()) { alert('Describe requested core changes first.'); return; }
    api.submitInaccuracyClaim(intakeId, inaccuracyComment, signoffToken, aeSigName || formState.ae)
      .then(d => {
        setCurrentIntakeStatus(d.status || 'inaccurate_data_claim');
        setShowInaccuracyBox(false);
        setInaccuracyComment('');
        alert('Inaccurate data claim submitted. The case is locked pending Contracts review.');
      })
      .catch(e => alert(`Claim dispatch failed: ${e.message}`));
  };

  const handleSendAeNotification = async () => {
    const emailTarget = aeEmail.trim();
    const nameTarget = aeNotifName.trim() || 'there';

    if (!emailTarget || !intakeId) return;

    try {
      const statePayload = { deal: formState, contractType, sites, revisionFromNewQuote: isQuoteRevisionMode };
      await api.updateIntake(intakeId, statePayload);

      const response = await api.generateSignoffLink(intakeId, emailTarget, nameTarget);

      setCurrentIntakeStatus(response.status || 'pending_signoff');
      setGeneratedDeeplink(response.deeplink || `${window.location.origin}/?id=${intakeId}`);
      setAeLinkGenerated(true);

      if (nameTarget && nameTarget !== 'there') {
        setAeSigName(nameTarget);
      }

      recordAuditLog({
        category: 'signoff',
        action: 'Generated AE sign-off link',
        detail: `Generated explicit sign-off URL path tracking metrics to destination address: ${emailTarget}`,
        target: intakeId || '',
        quoteNum: formState.quoteNum,
        intakeId: intakeId || ''
      });

    } catch (err) {
      alert(`Handoff transmission failure loop trace: ${err.message}`);
    }
  };

  const executeSignatureWorkflow = () => {
    if (aeSigName.trim().length < 3) return;
    setAeSignedOff(true);
    setWfStep(2);
    setWfAuditLog([{ action: 'AE signed off', detail: `Certified by ${aeSigName} — creating implementation case tracking stack entry.`, color: 'var(--blue)', time: nowStr() }]);
    recordAuditLog({ category: 'signoff', action: 'AE signed off', detail: `AE typed sign-off (${aeSigName}) and submitted certification.`, target: intakeId || '', quoteNum: formState.quoteNum, intakeId: intakeId || '' });
    setTimeout(() => { commitFinalSignoffPayload(); }, 1000);
  };

  const commitFinalSignoffPayload = () => {
    if (finalSubmitInFlight) return;
    setFinalSubmitInFlight(true);
    const signaturePayload = { name: aeSigName, title: aeSigTitle, date: aeSigDate, token: signoffToken };
    api.submitSignoff(intakeId, signaturePayload)
      .then(d => {
        if (!d.caseNumber) throw new Error('System failed to extract allocated Case designation.');
        setCaseNumber(d.caseNumber);
        recordAuditLog({ category: 'signoff', action: 'Implementation case created', detail: `Implementation case ${d.caseNumber} created after AE sign-off.`, target: d.caseNumber, quoteNum: formState.quoteNum, intakeId: intakeId || '' });
        setFinalSubmitComplete(true);
        setWfStep(2);
        setShowWorkflowModal(false);
        setShowPreview(false);
        document.body.style.overflow = '';
      })
      .catch(e => {
        setFinalSubmitInFlight(false);
        alert(`Verification handshake rejected signature parameters: ${e.message}`);
      });
  };

  const clearSystemEcosystemState = () => {
    sessionStorage.removeItem('active_intake_id');
    setFormState({
      quoteNum: '', oppName: '', ae: '', effDate: '', endDate: '',
      tcv: '', parent: '', region: '', sfAcctId: '', ffNotes: '',
      cnotes: '', repQuote: '', trialStart: '', trialEnd: ''
    });
    setContractType('');
    setSites([]);
    setSelectedSiteIdx(null);
    setAiAlert({ show: false, msg: '' });
    setAiSitesAlert({ show: false, msg: '' });
    setLookupStatus({ color: '', text: '' });
    setExtractedData(null);
    setCurrentIntakeStatus('draft');
    setCurrentInaccuracy(null);
    setFinalSubmitComplete(false);
    setAeSignedOff(false);
    setAeLinkGenerated(false);
    setWfAuditLog([]);
    setWfStep(1);
    setAeSigName('');
    setCaseNumber('');
    setFinalSubmitInFlight(false);
    setDuplicatePrompt(null);
    setOverrideInFlight(false);
    document.body.classList.remove('ae-review-only');
    document.body.style.overflow = '';

  };

  // Leaves the sign-off completion page: wipes the finished intake (including the
  // consumed deep-link token and URL params) and routes to the requested tab.
  const exitSignoffCompletion = (targetTab) => {
    clearSystemEcosystemState();
    setIntakeId(null);
    setSignoffToken(null);
    setIsSignoffMode(false);
    setShowPreview(false);
    window.history.replaceState(null, '', '/');
    setActiveTab(targetTab);
  };


  // ═══ ACTIVE ROUTER TERMINAL SEGMENTS VIEW REDIRECTS ═══
  if (activeTab === 'audit') return <AuditPortal onTabChange={(tab) => routeAndResetIntakeForm(tab)} />;
  if (activeTab === 'checklist') return <ServiceInstallChecklist onTabChange={(tab) => routeAndResetIntakeForm(tab)} />;
  if (activeTab === 'queue') return <CaseQueue onTabChange={(tab) => routeAndResetIntakeForm(tab)} />;
  if (activeTab === 'install-complete') return <InstallComplete onTabChange={(tab) => routeAndResetIntakeForm(tab)} />;

  if (finalSubmitComplete) {
    return (
      <div id="submittedView" style={{ display: 'block' }}>
        <div className="submitted-card">
          <div className="check-circle">✓</div>
          <h2>Signed off &amp; sent to Implementation Queue</h2>
          <p>Your sign-off is recorded and a case has been created in the Implementation Queue.</p>
          <div id="caseNum" className="mono">{caseNumber}</div>
          <div className="submitted-meta">
            <div className="sm-item"><div className="sm-label">Routed to</div><div className="sm-val" style={{ color: 'var(--green)' }}>Implementation Queue</div></div>
            <div className="sm-item"><div className="sm-label">Opportunity</div><div className="sm-val">{formState.oppName || '—'}</div></div>
            <div className="sm-item"><div className="sm-label">Case</div><div className="sm-val" style={{ color: 'var(--green)' }}>{caseNumber}</div></div>
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '8px' }}>
            {canCreateIntake(role) && (
              <button className="btn-main btn-ghost" onClick={() => exitSignoffCompletion('intake')}>New intake form</button>
            )}
            <button className="btn-main btn-ghost" onClick={() => exitSignoffCompletion(defaultTabForRole(role))}>Close &amp; go home</button>
            <button className="btn-main btn-cta" onClick={() => downloadFile(api.getReviewPdfUrl(intakeId), `${intakeId}-review.pdf`)}>Print / export PDF</button>
          </div>
        </div>
      </div>
    );
  }

  

  const hasOpenInaccuracyClaim = currentIntakeStatus === 'inaccurate_data_claim' || currentIntakeStatus === 'new_quote_needed';

  return (
    <div className="app-container">
      {/* SF TOP BAR CHROME CHASSIS LAYER */}
      <div id="chrome">
        <div className="chrome-logo"><span>SF</span> Sales Cloud</div>
        <div className="chrome-nav">
          {CHROME_TABS.filter(t => isTabAllowed(role, t.id)).map(t => (
            <button key={t.id} type="button" className={`chrome-nav-item ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>{t.label}</button>
          ))}
        </div>
        <div className="chrome-crumb">
          <span>Dashboard</span><span className="chrome-sep">›</span>
          <b id="crumbOpp">{formState.oppName || 'New Opportunity'}</b><span className="chrome-sep">›</span>
          <span>Implementation Intake</span>
          <span style={{ marginLeft: '8px' }}>
            <span className={`status-badge sb-${currentIntakeStatus === 'draft' ? 'draft' : 'pending'}`}>● {currentIntakeStatus.replace('_', ' ').toUpperCase()}</span>
          </span>
        </div>
        <div className="chrome-actions">
          {!isSignoffMode && <button className="sf-btn sf-btn-ghost" onClick={() => alert('Draft saved.')}>Save draft</button>}
          <button className="sf-btn sf-btn-primary" onClick={triggerReviewPreview}>Preview &amp; sign off →</button>
        </div>
      </div>

      <div id="topProgress"><div id="topProgressFill" style={{ width: `${progressPercentage}%` }}></div></div>

      <div id="mainWrap">
        {/* INTERACTIVE SIDE NAV WITH METRICS CONTAINER CARD */}
        <div id="sideNav">
          <div className="sidenav-card">
            <div className="sidenav-header">Form steps</div>
            <div className="sidenav-item active" onClick={() => handleScrollToSection('s0')}>
              <div className="sidenav-num" style={{ background: 'linear-gradient(135deg,var(--purple),#4F46E5)', color: '#fff', fontSize: '9px', fontWeight: 700 }}>AI</div>
              <div><div className="sidenav-label">Contract reader</div><div className="sidenav-sub">Upload &amp; parse PDF</div></div>
            </div>
            <div className="sidenav-item" onClick={() => handleScrollToSection('s1')}>
             <div className="sidenav-num"><span>1</span></div>
              <div><div className="sidenav-label">Deal information</div><div className="sidenav-sub">Quote, dates, value</div></div>
            </div>
            <div className="sidenav-item" onClick={() => handleScrollToSection('s2')}>
             <div className="sidenav-num"><span>2</span></div>
              <div><div className="sidenav-label">Account hierarchy</div><div className="sidenav-sub">Parent, region</div></div>
            </div>
            <div className="sidenav-item" onClick={() => handleScrollToSection('s3')}>
             <div className="sidenav-num"><span>3</span></div>
              <div><div className="sidenav-label">Contract type</div><div className="sidenav-sub">Type, notes</div></div>
            </div>
            <div className="sidenav-item" onClick={() => handleScrollToSection('s4')}>
             <div className="sidenav-num"><span>4</span></div>
              <div><div className="sidenav-label">Sites & modules</div><div className="sidenav-sub">Sites, modules & submit</div></div>
            </div>
          </div>

          <div className="summary-card" id="summaryCard">
            <h4>Summary</h4>
            <div className="summary-row"><span className="summary-label">Sites</span><span className="summary-val">{metrics.totalSites}</span></div>
            <div className="summary-row"><span className="summary-label">Activations</span><span className="summary-val">{metrics.totalActivations}</span></div>
            <div className="summary-row"><span className="summary-label">Contract type</span><span className="summary-val">{CT_LABELS[contractType] || '—'}</span></div>
            <div className="summary-row"><span className="summary-label">TCV Total</span><span className="summary-val green">{formState.tcv || '—'}</span></div>
          </div>
        </div>

        <div id="mainContent">
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--g900)', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'var(--red)', display: 'inline-block', flexShrink: 0 }}></span>
              Implementation Intake Form
              <span id="oppPill" style={{ fontFamily: 'var(--mono)', fontSize: '11px', padding: '4px 12px', borderRadius: '20px', background: 'var(--blue-l)', color: 'var(--blue)', border: '1px solid var(--blue-b)', fontWeight: 500, marginLeft: '4px' }}>
                {formState.oppName || 'Opportunity name'}
              </span>
            </div>
            <div style={{fontSize:'12px',color:'var(--g500)'}}>Complete all steps, preview your submission, then route directly to the implementation team</div>
          </div>

          {/* AI UPLOAD INTERACTIVE CARD ZONE */}
          <div className="card" id="s0">
            <div className="card-header">
              <div className="card-title"><div className="step-num ai">AI</div>AI contract reader</div>
              <span className="card-hint">Upload a signed contract — Claude reads and fills all fields</span>
            </div>
            <div className="card-body">
              {aiAlert.show && <div className="alert a-green"><span className="alert-icon">✓</span><span dangerouslySetInnerHTML={{ __html: aiAlert.msg }}></span></div>}
              
              {uploadState.parsing && (
                <div className="parse-panel show">
                  <div className="spinner"></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#5B21B6', marginBottom: '2px' }}>{uploadState.title}</div>
                    <div className="parse-progress-bar"><div className="parse-progress-fill" style={{ width: `${uploadState.pct}%` }}></div></div>
                  </div>
                </div>
              )}  
              {showUploadZone && (
                <div className="upload-zone" id="uploadZone" onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); processUploadedContract(e.dataTransfer.files[0]); }}>
                  <input type="file" onChange={e => processUploadedContract(e.target.files[0])} accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg" />
                  <div className="upload-zone-icon">📄</div>
                  <h3>Drop contract here or click to browse</h3>
                  <div className="upload-fmt">PDF · DOCX · DOC · TXT · PNG · JPG</div>
                </div>
              )}

              {extractedData && (
                <div className="extract-panel show">
                  <div className="extract-header">
                    <span style={{ fontSize: '16px', color: 'var(--green)' }}>✓</span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--green)', flex: 1 }}>
                      Parsed "{uploadState.filename}" — review highlighted fields
                    </span>
                    <span className={`conf-pill conf-${extractedData.confidence === 'high' ? 'high' : extractedData.confidence === 'medium' ? 'med' : 'low'}`}>
                      {(extractedData.confidence || 'low').charAt(0).toUpperCase() + (extractedData.confidence || 'low').slice(1)} Confidence
                    </span>
                    <button type="button" className="tb" style={{ marginLeft: '8px', fontSize: '10px' }} onClick={() => {setShowUploadZone(true);setExtractedData(null)}}>
                      Try different file
                    </button>
                  </div>

                  <div className="extract-body" id="extractBody">
                    <div className="extract-grid">
                      {[
                        ['Quote #', extractedData.quoteNumber || extractedData.quoteNum],
                        ['Opportunity', extractedData.opportunityName],
                        ['AE', extractedData.accountExecutive],
                        ['Parent Account', extractedData.parentAccount],
                        ['Contract Value', extractedData.totalContractValue|| '$0.00'],
                        ['Effective Date', extractedData.effectiveDate],
                        ['End Date', extractedData.endDate],
                        ['Contract Type', extractedData.contractType],
                        ['Trial Start', extractedData.trialStartDate || extractedData.trialStart],
                        ['Trial End', extractedData.trialEndDate || extractedData.trialEnd],
                        ['Sites', extractedData.sites && extractedData.sites.length 
                          ? `${extractedData.sites.length} site${extractedData.sites.length !== 1 ? 's' : ''}` 
                          : null
                        ]
                      ].map(([label, val], idx) => (
                        <div key={idx} className={`ef ${val ? 'hit' : ''}`}>
                          <div className="ef-label">{label}</div>
                          <div className="ef-val">{val || 'Not found'}</div>
                        </div>
                      ))}
                    </div>

                    {detectedModules.length > 0 && (
                      <div style={{ borderTop: '1px solid var(--g100)', paddingTop: '9px', marginTop: '5px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--g700)', marginBottom: '5px' }}>
                          Modules detected:
                        </div>
                        <div>
                          {detectedModules.map(m => (
                            <span key={m.id} className="mod-tag">
                              {m.l}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {extractedData.warnings && extractedData.warnings.length > 0 && (
                      <details style={{ borderTop: '1px solid var(--g100)', paddingTop: '9px', marginTop: '9px' }}>
                        <summary style={{ fontSize: '11px', fontWeight: 600, color: 'var(--amber)', marginBottom: '4px', cursor: 'pointer' }}>
                          Items to review ({extractedData.warnings.length})
                        </summary>
                        <div style={{ paddingTop: '4px' }}>
                          {extractedData.warnings.map((w, wIdx) => (
                            <div key={wIdx} style={{ fontSize: '11px', color: 'var(--g500)', marginBottom: '3px' }}>
                              &bull; {w}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CARD STEP 1: CONTEXT-GLOW FIELD MATRIX */}
          <div className="card" id="s1">
            <div className="card-header"><div className="card-title"><div className="step-num">1</div> Deal information</div></div>
            <div className="card-body">
              <div className="grid3" style={{ marginBottom: '14px' }}>
                <div className="field">
                  <label className="lbl">Quote # <span className="req">*</span></label>
                  <div className="input-with-btn">
                    <input type="text" className={aiHighlightedFields.quoteNum ? 'ai-glow' : ''} value={formState.quoteNum} onChange={e => handleInputChange('quoteNum', e.target.value)} />
                    <button type="button" className="tb blue" onClick={lookupQuoteSF}>Look up →</button>
                  </div>
                  {lookupStatus.text && <div style={{ fontSize: '11px', marginTop: '3px', color: lookupStatus.color }}>{lookupStatus.text}</div>}
                </div>
                <div className="field">
                  <label className="lbl">Opportunity name <span className="req">*</span> {aiHighlightedFields.oppName && <span className="ai-badge" style={{ display: 'inline' }}>AI</span>}</label>
                  <input type="text" className={aiHighlightedFields.oppName ? 'ai-glow' : ''} value={formState.oppName} onChange={e => handleInputChange('oppName', e.target.value)} />
                </div>
                <div className="field">
                  <label className="lbl">Account executive <span className="req">*</span> {aiHighlightedFields.ae && <span className="ai-badge" style={{ display: 'inline' }}>AI</span>}</label>
                  <input type="text" className={aiHighlightedFields.ae ? 'ai-glow' : ''} value={formState.ae} onChange={e => handleInputChange('ae', e.target.value)} />
                </div>
              </div>
              <div className="grid3">
                <div className="field">
                  <label className="lbl">Effective date {aiHighlightedFields.effDate && <span className="ai-badge" style={{ display: 'inline' }}>AI</span>}</label>
                  <input type="date" className={aiHighlightedFields.effDate ? 'ai-glow' : ''} value={formState.effDate} onChange={e => handleInputChange('effDate', e.target.value)} />
                </div>
                <div className="field">
                  <label className="lbl">End / renewal date {aiHighlightedFields.endDate && <span className="ai-badge" style={{ display: 'inline' }}>AI</span>}</label>
                  <input type="date" className={aiHighlightedFields.endDate ? 'ai-glow' : ''} value={formState.endDate} onChange={e => handleInputChange('endDate', e.target.value)} />
                </div>
                <div className="field">
                  <label className="lbl">Total contract value {aiHighlightedFields.tcv && <span className="ai-badge" style={{ display: 'inline' }}>AI</span>}</label>
                  <input type="text" className={aiHighlightedFields.tcv ? 'ai-glow' : ''} value={formState.tcv} onChange={e => handleInputChange('tcv', e.target.value)} placeholder="$0.00" />
                </div>
              </div>
            </div>
          </div>

          {/* CARD STEP 2: ACCOUNT HIERARCHY */}
          <div className="card" id="s2">
            <div className="card-header"><div className="card-title"><div className="step-num">2</div> Account hierarchy</div></div>
            <div className="card-body">
              <div className="grid3">
                <div className="field">
                  <label className="lbl">Ultimate parent account <span className="req">*</span> {aiHighlightedFields.parent && <span className="ai-badge" style={{ display: 'inline' }}>AI</span>}</label>
                  <input type="text" className={aiHighlightedFields.parent ? 'ai-glow' : ''} value={formState.parent} onChange={e => handleInputChange('parent', e.target.value)} />
                </div>
                <div className="field">
                  <label className="lbl">Region / sub-system {aiHighlightedFields.region && <span className="ai-badge" style={{ display: 'inline' }}>AI</span>}</label>
                  <input type="text" className={aiHighlightedFields.region ? 'ai-glow' : ''} value={formState.region} onChange={e => handleInputChange('region', e.target.value)} placeholder="e.g. Columbia, Missouri" />
                </div>
                <div className="field">
                  <label className="lbl">Salesforce account ID</label>
                  <input type="text" value={formState.sfAcctId} onChange={e => handleInputChange('sfAcctId', e.target.value)} placeholder="001XXXXXXXXXXXXXXX" />
                </div>
              </div>
            </div>
          </div>

          {/* CARD STEP 3: CONTRACT CHIP SELECTION & NOTICES */}
          <div className="card" id="s3">
            <div className="card-header"><div className="card-title"><div className="step-num">3</div> Contract type &amp; authorization</div></div>
            <div className="card-body">
              <div className="field" style={{ marginBottom: '16px' }}>
                <label className="lbl">Contract type <span className="req">*</span></label>
                <div className="chip-wrap" id="ctChips">
                  {Object.entries(CT_LABELS).map(([key, value]) => (
                    <button key={key} type="button" className={`chip ${contractType === key ? 'on' : ''}`} onClick={() => { setContractType(key); recordAuditLog({ category: 'field', action: 'Selected contract type', detail: `Contract type set to ${value}.`, target: key, quoteNum: formState.quoteNum, intakeId: intakeId || '' }); }}>{value}</button>
                  ))}
                </div>
              </div>

              {contractType === 'replacement' && (
                <>
                  <div className="alert a-amber"><span className="alert-icon">!</span><span><strong>Replacement quote</strong> — enter prior parameters references below.</span></div>
                  <div className="field" id="repField" style={{ marginBottom: '14px' }}>
                    <label className="lbl">Supersedes quote # <span className="req">*</span></label>
                    <input type="text" value={formState.repQuote} onChange={e => handleInputChange('repQuote', e.target.value)} style={{ maxWidth: '340px' }} />
                  </div>
                </>
              )}

              {contractType === 'termination' && (
                <div className="alert a-red"><span className="alert-icon">!</span><span><strong>Partial termination</strong> — specify modules context to switch OFF in Step 4.</span></div>
              )}

              {contractType === 'research' && (
                <div className="alert a-teal"><span className="alert-icon">🔬</span><span><strong>Research</strong> — include IRB documentation metadata references inside special terms notes block.</span></div>
              )}

              {contractType === 'evaluation' && (
                <div className="trial-section open">
                  <div className="alert a-teal" style={{ marginBottom: '14px' }}><span className="alert-icon">📋</span><span><strong>Evaluation / trial</strong> — specify the trial window. Access expires automatically on Trial End Date.</span></div>
                  <div className="grid2" style={{ maxWidth: '420px', marginBottom: '16px' }}>
                    <div className="field">
                      <label className="lbl">Trial start date <span className="req">*</span></label>
                      <input type="date" value={formState.trialStart} onChange={e => handleInputChange('trialStart', e.target.value)} />
                    </div>
                    <div className="field">
                      <label className="lbl">Trial end date <span className="req">*</span></label>
                      <input type="date" className={aiHighlightedFields.trialEndDate ? 'ai-glow' : ''} value={formState.trialEnd} onChange={e => handleInputChange('trialEnd', e.target.value)} />
                    </div>
                  </div>
                </div>
              )}

              <hr className="dv" />
              <div className="grid2">
                <div className="field">
                  <label className="lbl">Features &amp; Functionality notes {aiHighlightedFields.ffNotes && <span className="ai-badge" style={{ display: 'inline' }}>AI</span>}</label>
                  <textarea className={aiHighlightedFields.ffNotes ? 'ai-glow' : ''} value={formState.ffNotes} onChange={e => handleInputChange('ffNotes', e.target.value)} />
                </div>
                <div className="field">
                  <label className="lbl">Special terms / contract notes {aiHighlightedFields.cnotes && <span className="ai-badge" style={{ display: 'inline' }}>AI</span>}</label>
                  <textarea className={aiHighlightedFields.cnotes ? 'ai-glow' : ''} value={formState.cnotes} onChange={e => handleInputChange('cnotes', e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          {/* CARD STEP 4: SELECTION LEGENDS AND TABLE GRID IMPLEMENTATION */}
          <div className="card" id="s4" style={{ padding: 0 }}>
            <div className="card-header" style={{ padding: '14px 20px' }}>
              <div className="card-title">
                <div className="step-num">4</div> Sites &amp; modules — implementation matrix 
                <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--g500)', marginLeft: '4px' }}>( {metrics.totalSites} sites active )</span>
              </div>
            </div>
            
            <div style={{ padding: '12px 20px 0' }}>
              {aiSitesAlert.show && <div className="alert a-green"><span className="alert-icon">✓</span><span dangerouslySetInnerHTML={{ __html: aiSitesAlert.msg }}></span></div>}
              
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '11px', color: 'var(--g500)', fontWeight: 500 }}>Add row:</span>
                {['Hub', 'Spoke', 'CAH/FSED', 'MSTU', 'Clinic'].map(t => (
                  <button key={t} type="button" className="tb blue" onClick={() => addMatrixRow(t)}>+ {t}</button>
                ))}
                <button type="button" className="tb" onClick={() => addMatrixRow('')}>+ Blank</button>
                
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <button type="button" className="tb green" onClick={bulkCheckAllModules} disabled={selectedSiteIdx === null}>Check all</button>
                  <button type="button" className="tb" onClick={bulkClearAllModules} disabled={selectedSiteIdx === null}>Clear all</button>
                  <button type="button" className="tb" onClick={() => duplicateMatrixRow(selectedSiteIdx)} disabled={selectedSiteIdx === null}>Duplicate</button>
                  <button type="button" className="tb red" onClick={() => deleteMatrixRow(selectedSiteIdx)} disabled={selectedSiteIdx === null}>Delete</button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '10px', color: 'var(--g500)', paddingBottom: '10px', borderBottom: '1px solid var(--g100)', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, color: 'var(--g700)' }}>Groups:</span>
                {GO.map(grp => (
                  <span key={grp} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <i style={{ width: '9px', height: '9px', borderRadius: '2px', background: GB[grp], border: `1px solid ${GF[grp]}`, display: 'inline-block' }}></i>
                    {GL[grp]}
                  </span>
                ))}
              </div>
            </div>

            <div className="matrix-outer">
              <table className="mx">
                <thead>
                  <tr>
                    <th className="frz" style={{ left: 0, minWidth: '32px' }} rowSpan="2"></th>
                    <th className="frz" style={{ left: '32px', minWidth: '160px', textAlign: 'left' }} rowSpan="2">Site name</th>
                    <th className="frz" style={{ left: '192px', minWidth: '90px', textAlign: 'left' }} rowSpan="2">Type</th>
                    <th className="frz sep-r" style={{ left: '282px', minWidth: '100px', textAlign: 'left' }} rowSpan="2">Region</th>
                    {GO.map(grp => {
                      const count = MODS.filter(m => m.g === grp).length;
                      return <th key={grp} colSpan={count} style={{ background: GB[grp], color: GF[grp], textAlign: 'center', fontWeight: 600 }}>{GL[grp]}</th>;
                    })}
                    <th className="frz sep-l" style={{ right: '130px', minWidth: '60px' }} rowSpan="2">Scanners</th>
                    <th className="frz" style={{ right: 0, minWidth: '130px' }} rowSpan="2">Notes</th>
                  </tr>
                  <tr>
                    {MODS.map(m => (
                      <th key={m.id} style={{ background: GB[m.g], minWidth: '40px' }} title={m.l}>
                        <span className="rot" style={{ color: GF[m.g] }}>{m.l}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sites.map((s, sIdx) => (
                    <tr key={s.id} className={sIdx === selectedSiteIdx ? 'sel' : ''} onClick={() => setSelectedSiteIdx(sIdx)}>
                      <td className="frz" style={{ left: 0, textAlign: 'center' }}>
                        <button type="button" className="tb red" style={{ padding: '2px 6px' }} onClick={(e) => { e.stopPropagation(); deleteMatrixRow(sIdx); }}>×</button>
                      </td>
                      <td className="frz" style={{ left: '32px' }}>
                        <input className="mxi" type="text" value={s.name} onChange={e => { const next = [...sites]; next[sIdx].name = e.target.value; setSites(next); }} placeholder="Site name" />
                      </td>
                      <td className="frz" style={{ left: '192px', padding: '3px 4px' }}>
                        <select className="mxs" value={s.type} onChange={e => {
                          const t = e.target.value; const next = [...sites]; next[sIdx].type = t;
                          if (t && TD[t]) { const nm2 = {}; TD[t].forEach(x => { nm2[x] = true; }); next[sIdx].mods = nm2; }
                          setSites(next);
                        }}>
                          <option value="">—</option>
                          {['Hub', 'Spoke', 'CAH/FSED', 'MSTU', 'Clinic'].map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                        {s.type && <div style={{ marginTop: '2px' }}><span className="site-badge" style={{ background: TBC[s.type], color: TTC[s.type] }}>{s.type}</span></div>}
                      </td>
                      <td className="frz sep-r" style={{ left: '282px' }}>
                        <input className="mxi" type="text" value={s.region} onChange={e => { const next = [...sites]; next[sIdx].region = e.target.value; setSites(next); }} placeholder="Region" />
                      </td>
                      {MODS.map(m => {
                        const isChecked = !!s.mods[m.id];
                        return (
                          <td key={m.id} style={{ textAlign: 'center' }} className={isChecked ? 'ck' : ''} onClick={() => toggleMatrixCheckbox(sIdx, m.id)}>
                            <div className={`cbc ${isChecked ? 'ck' : ''}`}>
                              <input type="checkbox" checked={isChecked} readOnly />
                            </div>
                          </td>
                        );
                      })}
                      <td className="frz sep-l" style={{ right: '130px' }}>
                        <input className="mxi" style={{ textAlign: 'center' }} type="text" value={s.sc} onChange={e => { const next = [...sites]; next[sIdx].sc = e.target.value; setSites(next); }} placeholder="0" />
                      </td>
                      <td className="frz" style={{ right: 0 }}>
                        <input className="mxi" type="text" value={s.notes} onChange={e => { const next = [...sites]; next[sIdx].notes = e.target.value; setSites(next); }} placeholder="Notes…" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button type="button" className="add-row-btn" onClick={() => addMatrixRow('')}>+ Add another site row</button>
            
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '10px 20px', borderTop: '1px solid var(--g100)' }}>
              <div className="stat-pill">Sites: <strong>{metrics.totalSites}</strong></div>
              <div className="stat-pill">Hub: <strong>{metrics.Hub}</strong></div>
              <div className="stat-pill">Spoke: <strong>{metrics.Spoke}</strong></div>
              <div className="stat-pill">CAH/FSED: <strong>{metrics['CAH/FSED']}</strong></div>
              <div className="stat-pill">MSTU: <strong>{metrics['MSTU']}</strong></div>
              <div className="stat-pill">Clinic: <strong>{metrics['Clinic']}</strong></div>
              <div className="stat-pill">Module activations: <strong>{metrics.totalActivations}</strong></div>
            </div>
            
            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--g100)', background: 'linear-gradient(to bottom,var(--g50),#fff)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <span style={{ fontSize: '12px', color: 'var(--g500)' }}>Review sites and module selections, then preview and submit to the implementation team.</span>
              <button type="button" className="btn-main btn-cta" onClick={triggerReviewPreview}>Preview &amp; sign off →</button>
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER ACTION STICKY DASH BAR WRAPPER */}
      <div id="submitBar">
        <div className="submit-summary"><strong>{metrics.totalSites} sites · {metrics.totalActivations} activations</strong></div>
        <div className="submit-btns">
          <button type="button" className="btn-main btn-ghost" onClick={() => alert('Draft state cached into local memory database.')}>Save draft</button>
          <button type="button" className="btn-main btn-cta" onClick={triggerReviewPreview}>Preview &amp; sign off →</button>
        </div>
      </div>

      {/* COMPILATION REVIEW PREVIEW DIALOG DIRECTIVE (MODAL) */}
      {showPreview && (
        <div className="modal-overlay" onClick={() => { if (!isSignoffMode) setShowPreview(false); }}>
          <div className="pm-box" onClick={e => e.stopPropagation()}>
            <div className="pm-header">
              <div>
                <h2>📋 Review before submitting</h2>
                <p>Confirm all details below are correct — this will be routed directly to the implementation team</p>
              </div>
              {!isSignoffMode && <button className="pm-close" onClick={() => setShowPreview(false)}>×</button>}
            </div>
            <div className="pm-body">
              {/* Inaccurate-data / status banner */}
              {currentInaccuracy && currentInaccuracy.aeComment && (
                <div className="warn-banner" style={{
                  background: hasOpenInaccuracyClaim ? 'var(--red-l)' : 'var(--blue-l)',
                  borderColor: hasOpenInaccuracyClaim ? 'var(--red-b)' : 'var(--blue-b)',
                  color: hasOpenInaccuracyClaim ? 'var(--red-d)' : 'var(--blue)'
                }}>
                  <span style={{ fontSize: '15px' }}>{hasOpenInaccuracyClaim ? '!' : 'i'}</span>
                  <div>
                    <strong>{statusText(currentIntakeStatus)}:</strong>
                    <div style={{ marginTop: '5px', whiteSpace: 'pre-wrap', color: 'var(--g700)' }}>{currentInaccuracy.aeComment}</div>
                    {currentInaccuracy.imReason && (
                      <div style={{ marginTop: '8px' }}><strong>IM reason:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{currentInaccuracy.imReason}</span></div>
                    )}
                  </div>
                </div>
              )}

              {/* Missing-required-fields warning */}
              {previewWarnings.length > 0 && (
                <div className="warn-banner">
                  <span style={{ fontSize: '15px' }}>⚠</span>
                  <div>
                    <strong>Review before submitting:</strong>
                    <ul style={{ margin: '5px 0 0 16px', lineHeight: 1.8 }}>
                      {previewWarnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              {/* Deal header */}
              <div style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 24px', fontSize: '12px', color: 'var(--g700)', marginBottom: '6px' }}>
                  <span>Opp: <strong>{formState.oppName || '—'}</strong></span>
                  <span>Quote: <strong style={{ fontFamily: 'var(--mono)', fontSize: '11px' }}>{formState.quoteNum || '—'}</strong></span>
                  <span>AE: <strong>{formState.ae || '—'}</strong></span>
                  <span>Parent: <strong>{formState.parent || '—'}</strong></span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: '12px', color: 'var(--g700)', alignItems: 'center' }}>
                  {(formState.effDate || formState.endDate) && (
                    <span>Term: <strong>{formState.effDate || '—'} → {formState.endDate || '—'}</strong></span>
                  )}
                  {formState.tcv && <span>TCV: <strong style={{ color: 'var(--green)' }}>{formState.tcv}</strong></span>}
                  {contractType && (() => {
                    const ctColor = { evaluation: 'var(--teal)', research: 'var(--purple)', replacement: 'var(--amber)', termination: 'var(--red)' }[contractType] || 'var(--blue)';
                    const ctBg = { evaluation: 'var(--teal-l)', research: 'var(--purple-l)', replacement: 'var(--amber-l)', termination: 'var(--red-l)' }[contractType] || 'var(--blue-l)';
                    return <span style={{ background: ctBg, color: ctColor, padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>{CT_LABELS[contractType] || '—'}</span>;
                  })()}
                  {contractType === 'replacement' && formState.repQuote && (
                    <span style={{ fontSize: '11px', color: 'var(--amber)' }}>Supersedes: <strong>{formState.repQuote}</strong></span>
                  )}
                  {contractType === 'evaluation' && formState.trialStart && (
                    <span style={{ fontSize: '11px', color: 'var(--teal)' }}>Trial: <strong>{formState.trialStart} → {formState.trialEnd}</strong></span>
                  )}
                </div>
              </div>

              {/* Sites × modules matrix (grouped, color-coded) */}
              {!sites.length ? (
                <div style={{ textAlign: 'center', padding: '28px', color: 'var(--g400)', fontSize: '13px', border: '1px solid var(--g200)', borderRadius: '8px' }}>No sites added yet.</div>
              ) : (
                <>
                  <div style={{ overflowX: 'auto', border: '1px solid var(--g200)', borderRadius: '8px' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: '11px', whiteSpace: 'nowrap', width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ position: 'sticky', left: 0, zIndex: 4, background: '#F8F9FB', padding: '8px 12px', border: '1px solid var(--g200)', fontSize: '10px', color: 'var(--g500)', fontWeight: 600, textAlign: 'left', minWidth: '160px' }} rowSpan={2}>SITE NAME</th>
                          <th style={{ position: 'sticky', left: '160px', zIndex: 4, background: '#F8F9FB', padding: '8px 10px', border: '1px solid var(--g200)', fontSize: '10px', color: 'var(--g500)', fontWeight: 600, textAlign: 'left', minWidth: '70px' }} rowSpan={2}>TYPE</th>
                          <th style={{ position: 'sticky', left: '230px', zIndex: 4, background: '#F8F9FB', padding: '8px 10px', border: '1px solid var(--g200)', fontSize: '10px', color: 'var(--g500)', fontWeight: 600, textAlign: 'left', minWidth: '90px', borderRight: '2px solid var(--g300)' }} rowSpan={2}>REGION</th>
                          {GO.filter(grp => previewMatrix.groupSpans[grp]).map(grp => (
                            <th key={grp} colSpan={previewMatrix.groupSpans[grp]} style={{ background: GB[grp], color: GF[grp], padding: '7px 10px', border: '1px solid rgba(0,0,0,.06)', fontSize: '10px', fontWeight: 700, textAlign: 'center', letterSpacing: '.04em' }}>{(GL[grp] || grp).toUpperCase()}</th>
                          ))}
                        </tr>
                        <tr>
                          {previewMatrix.activeMods.map(m => (
                            <th key={m.id} title={m.l} style={{ background: GB[m.g], minWidth: '44px', maxWidth: '44px', height: '120px', verticalAlign: 'bottom', padding: '3px 2px 8px', border: '1px solid rgba(0,0,0,.06)' }}>
                              <span style={{ writingMode: 'vertical-rl', WebkitWritingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: '10px', fontWeight: 600, color: GF[m.g], display: 'block', maxHeight: '110px', overflow: 'hidden', lineHeight: 1.2 }}>{m.l}</span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sites.map((s, si) => {
                          const rowBg = si % 2 === 0 ? '#ffffff' : '#F9FAFB';
                          return (
                            <tr key={s.id}>
                              <td style={{ position: 'sticky', left: 0, zIndex: 2, background: rowBg, padding: '8px 12px', border: '1px solid var(--g100)', fontWeight: 600, fontSize: '12px' }}>
                                {s.name ? s.name : <em style={{ color: 'var(--red)' }}>No name</em>}
                              </td>
                              <td style={{ position: 'sticky', left: '160px', zIndex: 2, background: rowBg, padding: '8px 10px', border: '1px solid var(--g100)' }}>
                                {s.type ? <span style={{ background: TBC[s.type], color: TTC[s.type], padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: 600 }}>{s.type}</span> : <span style={{ color: 'var(--g400)' }}>—</span>}
                              </td>
                              <td style={{ position: 'sticky', left: '230px', zIndex: 2, background: rowBg, padding: '8px 10px', border: '1px solid var(--g100)', borderRight: '2px solid var(--g300)', fontSize: '11px', color: 'var(--g700)' }}>
                                {s.region ? s.region : <span style={{ color: 'var(--g300)' }}>—</span>}
                              </td>
                              {previewMatrix.activeMods.map(m => {
                                const checked = !!(s.mods && s.mods[m.id]);
                                const cellBg = checked ? (rowBg === '#ffffff' ? 'rgba(27,79,216,.04)' : 'rgba(27,79,216,.06)') : rowBg;
                                return (
                                  <td key={m.id} style={{ textAlign: 'center', padding: 0, border: '1px solid var(--g100)', background: cellBg }}>
                                    {checked && <span style={{ color: 'var(--blue)', fontSize: '14px', fontWeight: 700, display: 'block', lineHeight: '44px' }}>✓</span>}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Summary strip */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px', fontSize: '11px', color: 'var(--g500)' }}>
                    <span><strong style={{ color: 'var(--g900)' }}>{metrics.totalSites}</strong> site{metrics.totalSites !== 1 ? 's' : ''}</span>
                    <span style={{ color: 'var(--g300)' }}>·</span>
                    <span><strong style={{ color: 'var(--g900)' }}>{metrics.totalActivations}</strong> module activation{metrics.totalActivations !== 1 ? 's' : ''}</span>
                    <span style={{ color: 'var(--g300)' }}>·</span>
                    <span><strong style={{ color: 'var(--g900)' }}>{previewMatrix.distinctModules}</strong> distinct module{previewMatrix.distinctModules !== 1 ? 's' : ''}</span>
                  </div>
                </>
              )}

              {/* Notes */}
              {(formState.ffNotes || formState.cnotes) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '14px', borderTop: '1px solid var(--g100)', paddingTop: '14px' }}>
                  {formState.ffNotes && (
                    <div>
                      <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--g500)', marginBottom: '5px' }}>F&amp;F Notes</div>
                      <div style={{ fontSize: '12px', color: 'var(--g700)', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'var(--g50)', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--g100)' }}>{formState.ffNotes}</div>
                    </div>
                  )}
                  {formState.cnotes && (
                    <div>
                      <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--g500)', marginBottom: '5px' }}>Contract Notes</div>
                      <div style={{ fontSize: '12px', color: 'var(--g700)', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'var(--g50)', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--g100)' }}>{formState.cnotes}</div>
                    </div>
                  )}
                </div>
              )}

              {showInaccuracyBox && (
                <div className="pm-inaccuracy" style={{ marginTop: '20px', borderRadius: '8px', border: '1px solid var(--red-b)' }}>
                  <label>What data is inaccurate?</label>
                  <textarea value={inaccuracyComment} onChange={e => setInaccuracyComment(e.target.value)} placeholder="Describe the incorrect field, site, module, or quote detail. Include what should be corrected if known."></textarea>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '10px' }}>
                    <button className="btn-main btn-ghost" onClick={() => setShowInaccuracyBox(false)}>Cancel</button>
                    <button className="btn-main btn-danger" onClick={submitInaccuracyClaim}>Submit inaccurate data claim</button>
                  </div>
                </div>
              )}
            </div>
            <div className="pm-footer">
              <div style={{ fontSize: '12px', color: 'var(--g500)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {hasOpenInaccuracyClaim ? (
                  <>⚠️ {statusText(currentIntakeStatus)} must be resolved before sign-off.</>
                ) : (
                  <><span style={{ fontSize: '16px' }}>→</span> After sign-off, a case is created and sent directly to the Implementation Queue — no further approval needed</>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn-main btn-danger" onClick={() => setShowInaccuracyBox(true)}>Inaccurate Data</button>
                <button className="btn-main btn-ghost" onClick={() => { if (intakeId) downloadFile(api.getSignoffPdfUrl(intakeId), `${intakeId}-signoff.pdf`); }}>Download landscape review PDF</button>
                {!isSignoffMode ? (
                  <button className="btn-main btn-cta" onClick={() => { setShowPreview(false); setShowAeModal(true); }}>Prepare AE sign-off →</button>
                ) : (
                  <button className="btn-main btn-cta" onClick={() => { setShowWorkflowModal(true); }} disabled={hasOpenInaccuracyClaim}>Proceed to sign-off →</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UNIFIED SINGLE BLURRED OVERLAY CHASSIS (MAPPED DIRECTLY TO PREVIEW & SIGN OFF TRIGGER) */}
      {showAeModal && (
        <div className="modal-overlay show" style={{ display: 'flex', zIndex: 1000 }} onClick={() => { setShowAeModal(false); document.body.style.overflow = ''; }}>
          <div className="pm-box" style={{ maxWidth: '650px', width: '100%' }} onClick={e => e.stopPropagation()}>
            
            <div className="pm-header">
              <div>
                <h2>📧 Prepare AE sign-off handoff</h2>
                <p>Generate a sign-off link and download a landscape review PDF with fillable AE sign-off fields</p>
              </div>
              <button type="button" className="pm-close" onClick={() => {setShowAeModal(false); document.body.style.overflow = ''; }}>×</button>
            </div>

            <div className="pm-body" style={{ padding: '20px' }}>
              {aeLinkGenerated ? (
                <div id="aeNotifSentState">
                  <div style={{ background: '#EAFAF1', color: '#145A32', padding: '12px', borderRadius: '6px', marginBottom: '15px', fontSize: '13px' }}>
                    <strong>✓ Secure routing token generated successfully.</strong> Delivery pipelines are operating in manual override.
                  </div>
                  <div style={{ padding: '14px', background: 'var(--g50)', borderRadius: '6px', border: '1px solid var(--g200)' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '6px', color: 'var(--g700)' }}>🔗 Copy Direct Handoff Access Pointer Address:</div>
                    <a href={generatedDeeplink} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all', color: 'var(--blue)', fontSize: '12px', fontWeight: 500 }}>
                      {generatedDeeplink}
                    </a>
                  </div>
                </div>
              ) : (
                <div id="aeNotifForm">
                  <div className="field" style={{ marginBottom: '15px' }}>
                    <label className="lbl" style={{ fontWeight: 600, display: 'block', marginBottom: '5px' }}>AE email address <span style={{ color: 'red' }}>*</span></label>
                    <input 
                      type="email" 
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--g300)' }}
                      value={aeEmail} 
                      onChange={e => setAeEmail(e.target.value)} 
                      placeholder="ae@rapidai.com" 
                    />
                  </div>
                  
                  <div className="field" style={{ marginBottom: '20px' }}>
                    <label className="lbl" style={{ fontWeight: 600, display: 'block', marginBottom: '5px' }}>AE Name</label>
                    <input 
                      type="text" 
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--g300)' }}
                      value={aeNotifName} 
                      onChange={e => setAeNotifName(e.target.value)} 
                      placeholder="e.g. William Walsh" 
                    />
                  </div>

                  {/* REAL-TIME PREVIEW EMULATOR CARD */}
                  <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--g500)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '6px' }}>
                    Email Preview
                  </div>
                  <div className="email-preview-card" style={{ border: '1px solid var(--g200)', borderRadius: '6px', background: '#fff', overflow: 'hidden' }}>
                    <div className="ep-topbar" style={{ background: 'var(--g100)', padding: '8px 12px', borderBottom: '1px solid var(--g200)', display: 'flex', alignItems: 'center' }}>
                      <div className="ep-dots" style={{ display: 'flex', gap: '4px', marginRight: '8px' }}>
                        <div className="ep-dot" style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#FF5F56' }}></div>
                        <div className="ep-dot" style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#FFBD2E' }}></div>
                        <div className="ep-dot" style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#27C93F' }}></div>
                      </div>
                      <span style={{ fontSize: '11px', color: 'var(--g500)' }}>Email — Sign-off Notification</span>
                    </div>
                    
                    <div className="ep-body" style={{ padding: '15px', fontSize: '13px', lineHeight: '1.6' }}>
                      <div className="ep-meta" style={{ paddingBottom: '10px', borderBottom: '1px solid var(--g100)', marginBottom: '10px', fontSize: '11px' }}>
                        <span><strong>From:</strong> RapidAI Salesforce &lt;noreply@rapidai.com&gt;</span><br />
                        <span><strong>To:</strong> <span style={{ color: 'var(--blue)', fontWeight: 500 }}>{aeEmail || 'your@email.com'}</span></span><br />
                        <span><strong>Subject:</strong> <span style={{ fontWeight: 600 }}>[Action required] Sign off on implementation intake: {formState.oppName || 'your opportunity'}</span></span>
                      </div>
                      <p>Hi <strong>{aeNotifName || 'there'}</strong>,</p>
                      <p style={{ marginTop: '6px' }}>An implementation intake form for the above deal is ready for your sign-off. Please review the details and click below. While email sending is being set up, the IM can also attach a landscape review PDF with fillable sign-off fields for manual return.</p>
                      
                      <div style={{ background: 'var(--g50)', border: '1px solid var(--g200)', borderRadius: '6px', padding: '10px 12px', margin: '12px 0', fontSize: '11px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--g900)', marginBottom: '5px' }}>Deal Summary</div>
                        <div style={{ color: 'var(--g700)', lineHeight: '1.8' }}>
                          <div><strong>Opportunity:</strong> {formState.oppName || '—'}</div>
                          <div><strong>Parent Account:</strong> {formState.parent || '—'}</div>
                          <div><strong>Contract Type:</strong> {CT_LABELS[contractType] || '—'}</div>
                          <div><strong>TCV:</strong> <span style={{ color: 'var(--green)', fontWeight: 600 }}>{formState.tcv || '—'}</span></div>
                          <div><strong>Sites/modules:</strong> {metrics.totalSites} site{metrics.totalSites !== 1 ? 's' : ''} · {metrics.totalActivations} activations</div>
                        </div>
                      </div>

                      {/* <a 
                        href={`http://localhost:3005/?id=${encodeURIComponent(intakeId || '')}&review=1`}
                        target="_blank" 
                        rel="noreferrer"
                        className="ep-cta-btn" 
                        style={{ 
                            display: 'inline-block', 
                            padding: '6px 12px', 
                            background: 'var(--blue)', 
                            color: '#fff', 
                            borderRadius: '4px', 
                            fontSize: '11px', 
                            fontWeight: 600, 
                            textDecoration: 'none', 
                            cursor: 'pointer' 
                        }}
                        >
                        Open Form &amp; Sign Off →
                        </a> */}
                        <a 
                          className="ep-cta-btn" 
                          href="#" 
                          onClick={(event) => {
                            event.preventDefault(); // Prevents page jumping to href="#"
                            setShowAeModal(false);
                            setIsSignoffMode(true);
                            setShowPreview(true);
                          }}
                        >
                          Open form &amp; sign off →
                        </a>
                        
                      <div style={{ marginTop: '14px', fontSize: '11px', color: 'var(--g500)' }}>Or copy this direct link:</div>
                      {/* Fixed: Replaced un-declared function pass layout lookup link string expressions inline */}
                      <span className="ep-link" style={{ color: 'var(--blue)', fontSize: '11px', wordBreak: 'break-all' }}>
                        {window.location.origin}/?id={encodeURIComponent(intakeId || '')}&review=1
                      </span>
                    </div>
                    <div style={{ marginLeft: '14px', marginBottom: '14px', fontSize: '11px', color: 'var(--g500)' }}><span>This notification was sent by RapidAI Salesforce. The sign-off link will remain active until the form is signed.</span></div>
                  </div>
                </div>
              )}
            </div>

            <div className="pm-footer" style={{ padding: '15px 20px', background: 'var(--g50)', borderTop: '1px solid var(--g200)', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" className="btn-main btn-ghost" onClick={() => { setShowPreview(false); document.body.style.overflow = ''; }}>Cancel</button>
              {!aeLinkGenerated && (
                <button 
                  type="button" 
                  className="btn-main" 
                  onClick={handleSendAeNotification} 
                  disabled={!aeEmail.includes('@') || !aeEmail.includes('.')}
                >
                  Generate AE sign-off link
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* CRITICAL LEGAL SIGN-OFF CERTIFICATION CONSOLE */}
      {showWorkflowModal && (
        <div className="modal-overlay show" style={{ display: 'flex' }} onClick={() => { if (!aeSignedOff) setShowWorkflowModal(false); }}>
          <div className="wm-box" onClick={e => e.stopPropagation()}>
            <div className="wm-header">
              <div>
                <h2>✍ Sign off &amp; route to implementation</h2>
                <p>{aeSignedOff ? 'Creating the implementation case...' : 'Sign off to certify this intake is accurate — it will be sent directly to the Implementation Queue as a case'}</p>
              </div>
              {!aeSignedOff && <button type="button" className="wm-close" onClick={() => setShowWorkflowModal(false)}>×</button>}
            </div>

            {/* Step tracker — 2 nodes with sublabels */}
            <div className="wf-tracker">
              <div className={`wf-node ${wfStep > 1 ? 'done' : ''}`}>
                <div className={`wf-bubble ${wfStep > 1 ? 'done' : 'active'}`}>✍</div>
                <div className="wf-label">AE Sign-off</div>
                <div className="wf-sub">{wfStep > 1 ? 'Signed ✓' : 'Pending'}</div>
              </div>
              <div className="wf-node">
                <div className={`wf-bubble ${wfStep > 1 ? 'done' : 'idle'}`}>→</div>
                <div className="wf-label">Implementation Queue</div>
                <div className="wf-sub">{wfStep > 1 ? 'Creating case' : 'After sign-off'}</div>
              </div>
            </div>

            <div className="wm-body">
              <div className={`so-panel ${aeSignedOff ? 'completed' : ''}`}>
                <h4><span className="so-role ae">AE</span> Account Executive Certification</h4>
                <p>By signing below, I certify that this intake form is accurate and complete and the contract has been fully executed. Signing will immediately create an implementation case and route it to the Implementation Queue.</p>
                <div className="so-fields" style={{ marginTop: '12px' }}>
                  <div className="so-field">
                    <label>Full name — type to sign <span style={{ color: 'var(--red)' }}>*</span></label>
                    <input type="text" className={`sig-field ${aeSignedOff ? 'ae-signed' : ''}`} value={aeSigName} onChange={e => setAeSigName(e.target.value)} placeholder="Type your full name…" readOnly={aeSignedOff} />
                  </div>
                  <div className="so-field"><label>Title</label><input type="text" value={aeSigTitle} onChange={e => setAeSigTitle(e.target.value)} placeholder="Account Executive" readOnly={aeSignedOff} /></div>
                  <div className="so-field"><label>Date</label><input type="date" value={aeSigDate} onChange={e => setAeSigDate(e.target.value)} readOnly={aeSignedOff} /></div>
                </div>
                {!aeSignedOff ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <button type="button" className="btn-sign" onClick={executeSignatureWorkflow} disabled={aeSigName.trim().length < 3}>✍ Sign off &amp; send to Implementation Queue</button>
                    <span className="hint-text" style={{ color: aeSigName.trim().length >= 3 ? 'var(--green)' : 'var(--g400)', margin: 0 }}>
                      {aeSigName.trim().length >= 3 ? 'Ready — click to sign off and submit for review' : 'Type your full name above to enable'}
                    </span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--green)', fontSize: '13px', fontWeight: 600, padding: '8px 0' }}>
                    <span style={{ fontSize: '18px' }}>✓</span> Signed by {aeSigName} &nbsp;·&nbsp; {wfAuditLog[0]?.time || nowStr()}
                  </div>
                )}
              </div>

              {/* Sign-off audit trail */}
              {wfAuditLog.length > 0 && (
                <div className="audit-trail">
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--g700)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>📋 Sign-off audit trail</div>
                  <div>
                    {wfAuditLog.map((e, i) => (
                      <div key={i} className="audit-row">
                        <div className="audit-dot" style={{ background: e.color }}></div>
                        <div className="audit-row-text">
                          <div className="audit-action">{e.action}</div>
                          <div className="audit-detail">{e.detail}</div>
                          <div className="audit-time">{e.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="wm-footer">
              <div style={{ fontSize: '12px', color: 'var(--g500)' }}>
                {aeSignedOff ? 'Sign-off recorded. Creating implementation case...' : 'Your sign-off creates an implementation case and routes it to the queue immediately.'}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {!aeSignedOff && <button type="button" className="btn-main btn-ghost" onClick={() => setShowWorkflowModal(false)}>← Back to preview</button>}
                {caseNumber && <button type="button" className="btn-main btn-cta" onClick={() => window.open(`https://rapidai.lightning.force.com/lightning/r/Case/${encodeURIComponent(caseNumber)}/view`, '_blank')}>View in Salesforce →</button>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ DUPLICATE QUOTE OVERRIDE PROMPT ═══ */}
      {duplicatePrompt && (
        <div className="modal-overlay show" style={{ display: 'flex', zIndex: 1000 }} onClick={dismissDuplicatePrompt}>
          <div className="pm-box" style={{ maxWidth: '560px', width: '100%' }} onClick={e => e.stopPropagation()}>
            <div className="pm-header">
              <div>
                <h2>⚠ Duplicate quote detected</h2>
                <p>The uploaded contract matches a case that already exists</p>
              </div>
              {!overrideInFlight && <button type="button" className="pm-close" onClick={dismissDuplicatePrompt}>×</button>}
            </div>
            <div className="pm-body">
              <p style={{ fontSize: '13px', color: 'var(--g700)', marginBottom: '14px' }}>
                Quote <strong style={{ fontFamily: 'var(--mono)', fontSize: '12px' }}>{duplicatePrompt.quoteNum}</strong> already exists — do you want to overwrite it and start over?
              </p>
              <div style={{ border: '1px solid var(--g200)', borderRadius: 'var(--radius)', background: 'var(--g50)', padding: '12px 14px', marginBottom: '14px', display: 'flex', flexWrap: 'wrap', gap: '6px 24px', fontSize: '12px', color: 'var(--g700)' }}>
                <span>Status: <strong>{duplicatePrompt.existing.statusLabel || '—'}</strong></span>
                <span>Parent account: <strong>{duplicatePrompt.existing.parentAccount || '—'}</strong></span>
                <span>Case #: <strong style={{ fontFamily: 'var(--mono)', fontSize: '11px' }}>{duplicatePrompt.existing.caseNumber || '—'}</strong></span>
                <span>Opportunity: <strong>{duplicatePrompt.existing.opportunityName || '—'}</strong></span>
              </div>
              {duplicatePrompt.matchCount > 1 && (
                <div className="warn-banner">
                  <span style={{ fontSize: '15px' }}>⚠</span>
                  <div>{duplicatePrompt.matchCount} active cases share this quote number — overwriting archives all of them.</div>
                </div>
              )}
              <div className="warn-banner" style={{ background: 'var(--red-l)', borderColor: 'var(--red-b)', color: 'var(--red-d)' }}>
                <span style={{ fontSize: '15px' }}>!</span>
                <div>Overwriting archives the existing case and permanently deletes its install-checklist progress. This cannot be undone.</div>
              </div>
            </div>
            <div className="pm-footer">
              <button type="button" className="btn-main btn-ghost" onClick={openExistingCaseInstead} disabled={overrideInFlight}>No — open the existing case</button>
              <button type="button" className="btn-main btn-danger" onClick={confirmDuplicateOverride} disabled={overrideInFlight}>{overrideInFlight ? 'Overwriting…' : 'Yes — overwrite and start over'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}